import type { User } from '@auth0/auth0-spa-js';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiService } from '../api.service';
import { FrontendLoggerService } from '../frontend-logger.service';

export type AuthProfileReader = () => Observable<User | null>;

export class DashboardAccessEvaluator {
  constructor(
    private readonly apiService: ApiService,
    private readonly logger: FrontendLoggerService
  ) {}

  hasAccess(profileReader: AuthProfileReader): Observable<boolean> {
    return this.apiService.get<{ role?: string }>('user/profile').pipe(
      switchMap((user) => {
        if (this.hasDashboardRole(user?.role)) {
          this.logger.debug('Acceso dashboard evaluado', 'true');
          return of(true);
        }
        this.logger.warn(
          '[AuthService] user/profile did not grant dashboard access; checking Auth0 roles claim as fallback.',
          JSON.stringify(user)
        );
        return this.fromClaims(profileReader);
      }),
      catchError((error) => {
        this.logger.warn(
          '[AuthService] user/profile failed while evaluating dashboard access; falling back to Auth0 claims.',
          error?.message || JSON.stringify(error)
        );
        return this.fromClaims(profileReader);
      })
    );
  }

  fromClaims(profileReader: AuthProfileReader): Observable<boolean> {
    return profileReader().pipe(
      map((user) => {
        const roles = user?.['https://www.innovadoc-ia.com/roles']
          ?? user?.['https://your-app.com/roles'];
        const hasAccess = Array.isArray(roles)
          && roles.some((role) => this.hasDashboardRole(
            typeof role === 'string' ? role : null
          ));
        this.logger.debug('Acceso dashboard evaluado', String(hasAccess));
        return hasAccess;
      }),
      catchError((error) => {
        this.logger.error('Error hasDashboardAccess', error?.message || JSON.stringify(error));
        return of(false);
      })
    );
  }

  hasDashboardRole(role: string | null | undefined): boolean {
    const normalized = role?.trim().toLowerCase() ?? '';
    return normalized === 'admin'
      || normalized === 'executive'
      || normalized === 'admin-unknown';
  }
}
